import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Redirect, useLocation, useParams } from 'react-router';
import { z } from 'zod';
import { configurationChangelogObjectTypeSchema } from '@rossum/api-client/configurationChangelog';
import { PageLayout } from '@rossum/components/page-layout/PageLayout';
import { Box, Stack, Typography } from '@rossum/ui/material';
import { HelmetComponent } from '../../routes/HelmetComponent';
import { ConfigurationChangelogDetailHeader } from './ConfigurationChangelogDetailHeader';
import { SideBySideDiff } from './diff/SideBySideDiff';
import { SnapshotViewer } from './diff/SnapshotViewer';
import { FullTimestampTooltip } from './FullTimestampTooltip';
import { useConfigurationChangelogDetail } from './hooks/useConfigurationChangelogDetail';
import { configurationChangelogPath } from './routes';

const detailParamsSchema = z.object({
  versionId: z.coerce.number().int().positive(),
  objectType: configurationChangelogObjectTypeSchema,
  objectId: z.coerce.number().int().positive(),
});

const formatSnapshot = (snapshot: Record<string, unknown> | undefined) =>
  snapshot ? JSON.stringify(snapshot, null, 2) : '';

export const ConfigurationChangelogDetailPage = () => {
  const intl = useIntl();
  const { versionId } = useParams<{ versionId: string }>();
  const { search } = useLocation();

  const parsedParams = useMemo(() => {
    const searchParams = new URLSearchParams(search);
    return detailParamsSchema.safeParse({
      versionId,
      objectType: searchParams.get('objectType'),
      objectId: searchParams.get('objectId'),
    });
  }, [search, versionId]);

  // Workaround so only the diff scrolls: measure its top offset and size it to
  // the remaining viewport (fixing this properly would require PageLayout changes).
  const [contentTop, setContentTop] = useState(0);
  const measureContentTop = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setContentTop(Math.round(node.getBoundingClientRect().top));
    }
  }, []);

  const { data, isLoading, isError } = useConfigurationChangelogDetail({
    objectType: parsedParams.success ? parsedParams.data.objectType : 'hook',
    objectId: parsedParams.success ? parsedParams.data.objectId : 0,
    versionId: parsedParams.success ? parsedParams.data.versionId : 0,
    enabled: parsedParams.success,
  });

  if (!parsedParams.success) {
    return <Redirect to={configurationChangelogPath()} />;
  }

  const entries = data?.results ?? [];
  const currentEntry = entries[0];
  const previousEntry = entries[1];

  const currentSnapshot = formatSnapshot(currentEntry?.snapshot);
  const previousSnapshot = formatSnapshot(previousEntry?.snapshot);

  const isUpdate = currentEntry?.versionEvent === 'update';

  const title = currentEntry
    ? intl.formatDate(new Date(currentEntry.versionCreatedAt), {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : '';

  const objectName =
    currentEntry?.name ?? currentEntry?.objectId?.toString() ?? '';

  const withFullTimestamp = (label: string, createdAt: string) => (
    <FullTimestampTooltip createdAt={createdAt}>{label}</FullTimestampTooltip>
  );

  return (
    <>
      <HelmetComponent translationKey="features.configurationChangelog.header.title" />
      <PageLayout
        fullWidth
        renderHeader={params => (
          <ConfigurationChangelogDetailHeader
            {...params}
            objectName={objectName}
            versionDate={title}
          />
        )}
      >
        <Stack flex={1} p={4} spacing={2}>
          {isLoading ? (
            <Stack height="50vh" justifyContent="center" alignItems="center">
              <Typography variant="h6" color="text.secondary">
                {intl.formatMessage({
                  id: 'features.configurationChangelog.detail.loading',
                })}
              </Typography>
            </Stack>
          ) : isError || !currentEntry ? (
            <Stack height="50vh" justifyContent="center" alignItems="center">
              <Typography variant="h6" color="text.secondary">
                {intl.formatMessage({
                  id: 'features.configurationChangelog.detail.error',
                })}
              </Typography>
            </Stack>
          ) : (
            <Box
              ref={measureContentTop}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                // 32px matches the parent Stack's bottom padding (p={4}).
                height: `calc(100vh - ${contentTop}px - 32px)`,
                minHeight: 400,
              }}
            >
              {isUpdate && previousEntry ? (
                <SideBySideDiff
                  oldText={previousSnapshot}
                  newText={currentSnapshot}
                  beforeLabel={withFullTimestamp(
                    intl.formatMessage(
                      { id: 'features.configurationChangelog.detail.before' },
                      {
                        date: intl.formatDate(
                          new Date(previousEntry.versionCreatedAt),
                          { dateStyle: 'medium', timeStyle: 'short' }
                        ),
                      }
                    ),
                    previousEntry.versionCreatedAt
                  )}
                  afterLabel={withFullTimestamp(
                    intl.formatMessage(
                      { id: 'features.configurationChangelog.detail.after' },
                      {
                        date: intl.formatDate(
                          new Date(currentEntry.versionCreatedAt),
                          { dateStyle: 'medium', timeStyle: 'short' }
                        ),
                      }
                    ),
                    currentEntry.versionCreatedAt
                  )}
                />
              ) : (
                <SnapshotViewer
                  text={currentSnapshot}
                  label={withFullTimestamp(
                    intl.formatMessage(
                      { id: 'features.configurationChangelog.detail.snapshot' },
                      {
                        date: intl.formatDate(
                          new Date(currentEntry.versionCreatedAt),
                          { dateStyle: 'medium', timeStyle: 'short' }
                        ),
                      }
                    ),
                    currentEntry.versionCreatedAt
                  )}
                />
              )}
            </Box>
          )}
        </Stack>
      </PageLayout>
    </>
  );
};
